import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { DOWN_ARROW_KEY, ENTER_KEY, ESCAPE_KEY, SPACE_KEY, TAB_KEY, UP_ARROW_KEY } from '../../../const/keyNames';
import { dispatchEvent, isOptionEqual, useOutsideAlerter, useUpdateMenuPosition } from './helpers';
import OptionsMenu from './OptionsMenu';
import { FieldWrapper, HelpAndError, HelpTextWrapper, NativeSelect, SelectInnerWrapper, SelectOuterWrapper, SelectValue } from './subcomponents';
const Select = ({ id, name, error, options = [], placeholder = '', helpText, leftIcon = '', defaultOption, rightIconOpen = '', rightIconClosed = '', errorIcon = '', isDisabled = false, className = '', onChange = () => { }, onBlur = () => { }, placement = 'BOTTOM', autoAdjust, required = false, tabIndex = 0, selectInputRef, dataCy, dataType, dataObject, dataReason, dataAnalytics, ariaLabelledBy = `${id}-label`, ariaDescribedBy = `syf-help-text-${id}` }) => {
    const [isMenuOpen, setMenuOpen] = useState(false);
    const wrapperRef = useRef(null);
    const nativeSelectRef = useRef(null);
    const animateLabel = !isDisabled || error;
    const [selectedOption, setSelectedOption] = useState(defaultOption);
    const [focusedOption, setFocusedOption] = useState(selectedOption);
    const [shouldChangeBeDispatched, setChangeBeDispatched] = useState(false);
    const hasSelection = !!(selectedOption === null || selectedOption === void 0 ? void 0 : selectedOption.label);
    const [menuPosition, setMenuPosition] = useState(placement);
    const [activeDescendant, setActiveDescendant] = useState('');
    const defaultRef = useRef(null);
    const ref = selectInputRef !== null && selectInputRef !== void 0 ? selectInputRef : defaultRef;
    // Call back for positioning the menu placement based on bottom space and field position
    const getMenuPosition = useUpdateMenuPosition(options, setMenuPosition, isMenuOpen, autoAdjust);
    useOutsideAlerter(wrapperRef, () => setMenuOpen(false));
    const selectedOptionLabel = (selectedOption && selectedOption.label) || placeholder;
    const toggleOpen = () => setMenuOpen((prevMenuOpen) => !prevMenuOpen);
    const moveFocusUp = () => {
        // We firstly need to get the index of the current focused option
        const currFocusedOptionIndex = options.findIndex((option) => isOptionEqual(option, focusedOption));
        /**
         * When we press arrow UP, we need to move the focus to the option ABOVE the current one, so we want the focusedOptionIndex - 1
         * and we need to make sure we don't get a number smaller than 0 (because that wouldn't be a valid index in the array of options)
         */
        const newFocusedOptionIndex = currFocusedOptionIndex > 1 ? currFocusedOptionIndex - 1 : 0;
        const newFocusedOption = options[newFocusedOptionIndex];
        setFocusedOption(newFocusedOption);
        setActiveDescendant(`${id}-option-${newFocusedOptionIndex}`);
    };
    const moveFocusDown = () => {
        // We firstly need to get the index of the current focused option
        const currFocusedOptionIndex = options.findIndex((option) => isOptionEqual(option, focusedOption));
        /**
         * When we press arrow DOWN, we need to move the focus to the option BELOW the current one, so we want the focusedOptionIndex + 1
         * and we need to make sure we don't get a number greater than the last option index (because that wouldn't be a valid index in the array of options)
         */
        const lastOptionIndex = options.length - 1;
        const newFocusedOptionIndex = currFocusedOptionIndex + 1 <= lastOptionIndex
            ? currFocusedOptionIndex + 1
            : lastOptionIndex;
        const newFocusedOption = options[newFocusedOptionIndex];
        setFocusedOption(newFocusedOption);
        setActiveDescendant(`${id}-option-${newFocusedOptionIndex}`);
    };
    const selectOption = (option) => {
        if (option.onClick) {
            option.onClick();
        }
        setSelectedOption(option);
        toggleOpen();
    };
    const performAction = (key) => {
        var _a, _b;
        switch (key) {
            case UP_ARROW_KEY:
                moveFocusUp();
                break;
            case DOWN_ARROW_KEY:
                moveFocusDown();
                break;
            case TAB_KEY:
                setSelectedOption(focusedOption);
                toggleOpen();
                break;
            case ESCAPE_KEY:
                toggleOpen();
                (_a = ref === null || ref === void 0 ? void 0 : ref.current) === null || _a === void 0 ? void 0 : _a.focus();
                break;
            case ENTER_KEY:
            case SPACE_KEY:
                (_b = ref === null || ref === void 0 ? void 0 : ref.current) === null || _b === void 0 ? void 0 : _b.focus();
                selectOption(focusedOption);
                break;
            default:
                break;
        }
    };
    const handleWrapperOnKeyDown = (event) => {
        if (event.key !== TAB_KEY)
            event.preventDefault();
        const openKeys = [UP_ARROW_KEY, DOWN_ARROW_KEY, ENTER_KEY, SPACE_KEY];
        if (!isMenuOpen && openKeys.includes(event.key)) {
            toggleOpen();
        }
        else if (isMenuOpen) {
            performAction(event.key);
            event.stopPropagation();
        }
    };
    useEffect(() => {
        const hasDefaultOptionChanged = !isOptionEqual(selectedOption, defaultOption);
        if (defaultOption && hasDefaultOptionChanged) {
            setSelectedOption(defaultOption);
        }
        /*
         * We don't want to trigger a 'change' event when the default option is being set,
         * because no real 'change' event has happened. We start dispatching after first mount,
         * when this variable has been enabled..
         */
        setChangeBeDispatched(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultOption]);
    useEffect(() => {
        /*
         * the onChange callback prop is passed to the native select element,
         * but since we don't actually use that element to select a different
         * option we need to manually trigger an onChange event on it for the
         * callback to be executed
         */
        dispatchEvent(nativeSelectRef, shouldChangeBeDispatched);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedOption, id]);
    const isNotDefaultSelection = selectedOption && placeholder !== selectedOption.label;
    return (_jsxs(FieldWrapper, Object.assign({ onKeyDown: handleWrapperOnKeyDown, ref: wrapperRef, "data-cy": dataCy }, { children: [_jsx(NativeSelect, { id: `native-select-${id}`, nativeSelectRef: nativeSelectRef, name: name, required: required, error: error, tabIndex: tabIndex, onChange: onChange, options: options, selectedOption: selectedOption, ariaDescribedBy: ariaDescribedBy, dataCy: dataCy }), _jsxs(SelectOuterWrapper, Object.assign({ ref: getMenuPosition }, { children: [_jsx(SelectInnerWrapper, Object.assign({ ref: ref, error: error, isDisabled: isDisabled, "aria-required": required, "aria-disabled": isDisabled, tabIndex: tabIndex, "aria-expanded": isMenuOpen, "data-type": dataType, "data-object": dataObject, "data-reason": dataReason, "data-analytics": dataAnalytics, className: `Select__inner-wrapper ${className}`, onClick: () => !isDisabled && setMenuOpen(!isMenuOpen), role: "combobox", onBlur: onBlur, "aria-labelledby": ariaLabelledBy, "aria-describedby": ariaDescribedBy, "aria-haspopup": "listbox", "aria-controls": `${id}-options-inner-wrapper`, id: `${id}-combobox`, "aria-activedescendant": activeDescendant }, { children: _jsx(SelectValue, { isMenuOpen: isMenuOpen, animateLabel: animateLabel, hasSelection: hasSelection, selectedOptionLabel: selectedOptionLabel, placeholder: placeholder, id: id, name: name, dataCy: dataCy, leftIcon: leftIcon, rightIconClosed: rightIconClosed, rightIconOpen: rightIconOpen, isDisabled: isDisabled, error: error, isNotDefaultSelection: isNotDefaultSelection }) })), isMenuOpen && (_jsx(OptionsMenu, { menuPosition: menuPosition, options: options, selectedOption: selectedOption, focusedOption: focusedOption, onOptionClick: selectOption, id: id, ariaLabelledBy: ariaLabelledBy }))] })), _jsx(HelpTextWrapper, Object.assign({ "$isError": Boolean(error), "aria-live": "polite", "data-cy": dataCy && `${dataCy}.helptext` }, { children: (error || helpText) && (_jsx(HelpAndError, { id: ariaDescribedBy, error: error, helpText: helpText, errorIcon: errorIcon })) }))] })));
};
export default Select;
